import React, { useState } from 'react';
import { Phone, MessageCircle, Loader2 } from 'lucide-react';
import { PhoneInput } from '../common/PhoneInput';
import toast from '../../utils/toast';
import { verificationAPI } from '../../api/verification';
import { useAuth } from '../../contexts/AuthContext';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export const PhoneLogin: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [isLoading, setIsLoading] = useState(false);
  const { loginWithPhone } = useAuth();
  const navigate = useNavigate();

  const handleSendCode = async () => {
    setIsLoading(true);
    try {
      await verificationAPI.sendPhoneVerification(phone);
      toast.success('Code sent via WhatsApp');
      setStep('verify');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    setIsLoading(true);
    try {
      await loginWithPhone(phone, code);
      navigate('/products');
      // AuthContext will handle redirect
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {step === 'phone' ? (
        <>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            label="Phone Number"
            name="phone"
            required={true}
          />
          <button
            onClick={handleSendCode}
            disabled={isLoading || !phone}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <FaWhatsapp className="h-5 w-5 mr-2" />
            )}
            Send WhatsApp Code
          </button>
        </>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              6-Digit Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 text-center text-2xl tracking-widest"
              placeholder="123456"
              maxLength={6}
            />
            <p className="text-xs text-gray-500 mt-2">
              Code sent to {phone}
            </p>
          </div>
          <button
            onClick={handleVerify}
            disabled={isLoading || code.length !== 6}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? 'Verifying...' : 'Verify & Login'}
          </button>
          <button
            onClick={() => setStep('phone')}
            className="w-full text-sm text-gray-600 hover:text-gray-800"
          >
            ← Change number
          </button>
        </>
      )}
    </div>
  );
};